/* eslint-disable no-case-declarations */
import { useMemo, useState } from "react"

import type { GraphFileItem } from "@/api/models"

export type SortOption = "name" | "lastModified" | "type"
export type SortDirection = "asc" | "desc"

export interface FileSorting {
  option: SortOption
  direction: SortDirection
}

export function useFilePickerSorting(
  initialSorting: FileSorting = { option: "name", direction: "asc" }
) {
  const [sorting, setSorting] = useState<FileSorting>(initialSorting)

  const setSortOption = (option: SortOption) => {
    setSorting((prev) => {
      // If same option is selected, toggle direction
      if (prev.option === option) {
        return { ...prev, direction: prev.direction === "asc" ? "desc" : "asc" }
      }
      // If different option, use ascending by default
      return { option, direction: "asc" }
    })
  }

  const getFileType = (file: GraphFileItem): string => {
    // More robust folder detection
    if (file.isFolder === true || file.mimeType === "application/vnd.microsoft.graph.folder") {
      return "folder"
    }

    // Extract file extension from name or use mimeType
    const extension = file.name.split(".").pop()?.toLowerCase() || ""
    if (extension) return extension

    // Fallback to mimeType if no extension
    if (file.mimeType) {
      const typeCategory = file.mimeType.split("/")[1]
      return typeCategory || "file"
    }

    return "file"
  }

  const isFolder = (file: GraphFileItem): boolean => {
    return file.isFolder === true || file.mimeType === "application/vnd.microsoft.graph.folder"
  }

  const sortFiles = (files: GraphFileItem[]): GraphFileItem[] => {
    if (files.length === 0) return files

    return [...files].sort((a, b) => {
      let comparison = 0

      switch (sorting.option) {
        case "name":
          // Always sort folders first, then files
          if (isFolder(a) && !isFolder(b)) return -1
          if (!isFolder(a) && isFolder(b)) return 1
          // Then sort by name
          comparison = a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: "base",
          })
          break

        case "lastModified":
          // Always sort folders first, then files
          if (isFolder(a) && !isFolder(b)) return -1
          if (!isFolder(a) && isFolder(b)) return 1
          // Then sort by last modified date
          const dateA = new Date(a.lastModified || 0).getTime()
          const dateB = new Date(b.lastModified || 0).getTime()
          comparison = dateB - dateA // Most recent first by default
          break

        case "type":
          // Get file types
          const typeA = getFileType(a)
          const typeB = getFileType(b)

          // Always sort folders first
          if (typeA === "folder" && typeB !== "folder") return -1
          if (typeA !== "folder" && typeB === "folder") return 1

          // Then sort by type
          comparison = typeA.localeCompare(typeB)
          // If same type, sort by name
          if (comparison === 0) {
            comparison = a.name.localeCompare(b.name, undefined, {
              numeric: true,
              sensitivity: "base",
            })
          }
          break

        default:
          comparison = 0
      }

      // Apply sort direction (except for lastModified which defaults to desc)
      if (sorting.option === "lastModified") {
        return sorting.direction === "asc" ? -comparison : comparison
      } else {
        return sorting.direction === "asc" ? comparison : -comparison
      }
    })
  }

  const sortedFiles = useMemo(() => sortFiles, [sorting])

  return {
    sorting,
    setSorting,
    setSortOption,
    sortedFiles,
    sortFiles,
  }
}
